/**
 * Gamepad input (Phase 8) on the standard mapping: left stick moves, right stick aims (a squared
 * curve past the dead zone, applied as a turn rate), triggers fire, face buttons for jump,
 * melee, vent and ability, bumpers for boost and the ultimate, Start for the menu, Back for
 * the scoreboard, right stick click to swap shoulder. Pure: the controller polls the pads.
 */
import type { Action, GamepadSettings } from '@steelwake/shared/progression';

export interface GamepadLike {
  axes: readonly number[];
  buttons: readonly { pressed: boolean; value: number }[];
  mapping: string;
}

export interface GamepadSample {
  moveX: number;
  moveY: number;
  /** Aim rates -1..1 after the dead zone and the curve (right is positive, down is positive). */
  aimX: number;
  aimY: number;
  pressed: Set<Action>;
}

/** Radians per second of turn at full stick deflection and sensitivity 1. */
export const AIM_RATE = 3.2;

const STANDARD_BUTTONS: Readonly<Record<number, Action>> = {
  0: 'jump',
  1: 'melee',
  2: 'vent',
  3: 'ability',
  4: 'boost',
  5: 'ultimate',
  6: 'secondary',
  7: 'primary',
  8: 'scoreboard',
  9: 'menu',
  11: 'swapShoulder',
};

/** A stick past its radial dead zone, rescaled so the edge of the zone reads 0. */
export function stick(x: number, y: number, deadZone: number): { x: number; y: number } {
  const len = Math.hypot(x, y);
  if (len <= deadZone || len === 0) return { x: 0, y: 0 };
  const scaled = Math.min(1, (len - deadZone) / (1 - deadZone));
  return { x: (x / len) * scaled, y: (y / len) * scaled };
}

/** A squared response keeps small deflections fine and the edge fast. */
export function aimCurve(v: number): number {
  return v * Math.abs(v);
}

export function readGamepad(pad: GamepadLike, settings: GamepadSettings): GamepadSample {
  const move = stick(pad.axes[0] ?? 0, pad.axes[1] ?? 0, settings.deadZone);
  const aim = stick(pad.axes[2] ?? 0, pad.axes[3] ?? 0, settings.deadZone);
  const pressed = new Set<Action>();
  pad.buttons.forEach((button, index) => {
    const action = STANDARD_BUTTONS[index];
    if (action !== undefined && (button.pressed || button.value > 0.5)) pressed.add(action);
  });
  return {
    moveX: move.x,
    // The stick's up is negative; the simulation's forward is positive.
    moveY: -move.y,
    aimX: aimCurve(aim.x),
    aimY: aimCurve(aim.y),
    pressed,
  };
}

/** True when any stick or button is live, so a resting pad never overrides the keyboard. */
export function gamepadActive(sample: GamepadSample): boolean {
  return (
    sample.pressed.size > 0 ||
    sample.moveX !== 0 ||
    sample.moveY !== 0 ||
    sample.aimX !== 0 ||
    sample.aimY !== 0
  );
}
